import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Cancel01Icon } from '@hugeicons/core-free-icons';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
type FooterAlign = 'left' | 'center' | 'right';

@Component({
    selector: 'app-modal',
    standalone: true,
    imports: [CommonModule, HugeiconsIconComponent],
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    animations: [
        trigger('backdrop', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0 }))
            ])
        ]),
        trigger('modalContent', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-20px) scale(0.95)' }),
                animate('300ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(20px) scale(0.95)' }))
            ])
        ])
    ]
})
export class ModalComponent implements OnInit, OnDestroy {
    // Header inputs
    @Input() title: string = '';
    @Input() isOpen: boolean = false;
    @Input() size: ModalSize = 'md';
    @Input() showCloseButton: boolean = true;
    @Input() showHeaderLeft: boolean = false;
    @Input() closeOnBackdropClick: boolean = false;

    // Footer inputs
    @Input() showFooter: boolean = true;
    @Input() cancelButtonText: string = 'Cancel';
    @Input() submitButtonText: string = 'Submit';
    @Input() showCancelButton: boolean = true;
    @Input() showSubmitButton: boolean = true;
    @Input() submitButtonDisabled: boolean = false;
    @Input() footerAlign: FooterAlign = 'right';
    @Input() contentClass: string = '';
    /** When true, render content inline (no fixed wrapper / backdrop). */
    @Input() inline: boolean = false;

    @Output() close = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();
    @Output() submit = new EventEmitter<void>();

    // Icons
    closeIcon = Cancel01Icon;

    ngOnInit(): void {
        if (this.isOpen) {
            this.blockScroll();
        }
    }

    ngOnDestroy(): void {
        this.unblockScroll();
    }

    handleClose(): void {
        this.close.emit();
    }

    handleCancel(): void {
        this.cancel.emit();
    }

    handleSubmit(): void {
        this.submit.emit();
    }

    onBackdropClick(event: MouseEvent): void {
        if (!this.closeOnBackdropClick) return;
        if (!(event.target as HTMLElement).classList.contains('modal-backdrop')) return;
        this.handleClose();
    }

    private blockScroll(): void {
        if (this.inline) return;
        document.body.style.overflow = 'hidden';
    }

    private unblockScroll(): void {
        if (this.inline) return;
        document.body.style.overflow = '';
    }

    // Watch for isOpen changes to handle scroll locking
    ngOnChanges(): void {
        if (this.isOpen) {
            this.blockScroll();
        } else {
            this.unblockScroll();
        }
    }
}
